import {
  CM_PER_INCH,
  GARMENT_MEASUREMENTS,
  INTERNATIONAL_SIZE_CONVERSIONS,
  PROVISIONAL_FIT_CONFIGURATION,
  lengthReferenceForProfile,
  lengthTableReferenceForProfile,
  provisionalValueForDisplay,
  sizeDefinitionForValue,
  validGarmentCentimetres,
  validGarmentMeasurementData,
  validInternationalConversionData,
  weightReferenceForSize,
  weightTableReferenceForSize,
} from './velorten-fit-system.js';

export const SIZE_GUIDE_SECTIONS = Object.freeze([
  Object.freeze({ value: 'recommended', label: 'Recommended Fit' }),
  Object.freeze({ value: 'ranges', label: 'Height & Weight' }),
  Object.freeze({ value: 'garment', label: 'Garment Measurements' }),
  Object.freeze({ value: 'international', label: 'International Equivalents' }),
  Object.freeze({ value: 'measure', label: 'How to Measure' }),
  Object.freeze({ value: 'questions', label: 'Fit Questions', requires: 'questions' }),
]);

const normalize = (value) => String(value ?? '').trim().toLowerCase();
const normalizeId = (value) => String(value ?? '').replace(/^gid:\/\/shopify\/[A-Za-z]+\//, '').trim();

export function availableGuideSections(
  config,
  { hasQuestions = true } = {}
) {
  const availability = {
    questions: config?.fitQuestionsEnabled === true && hasQuestions,
  };
  return SIZE_GUIDE_SECTIONS.filter((section) => !section.requires || availability[section.requires]);
}

export function formatGarmentMeasurement(value, unit = 'cm') {
  const centimetres = validGarmentCentimetres(value);
  if (centimetres == null) return 'Pending';
  if (unit === 'in') return `${(centimetres / CM_PER_INCH).toFixed(1)} in`;
  return `${Number(centimetres.toFixed(2))} cm`;
}

export function recommendationFromSession(rawValue) {
  try {
    const state = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
    const recommendation = state?.recommendation;
    const size = String(state?.selectedResultSize || recommendation?.size || '').trim();
    const lengthProfile = String(recommendation?.length || '').trim();
    if (!sizeDefinitionForValue(size)) return null;
    if (!PROVISIONAL_FIT_CONFIGURATION.lengthProfiles.some((profile) => profile.value === lengthProfile)) return null;
    return { size, lengthProfile, source: 'find-my-fit' };
  } catch {
    return null;
  }
}

/**
 * Session-scoped, non-visual handoff for connected fit experiences.
 * Personal measurements remain in the browser session and are not written to
 * DOM attributes, cart properties, query strings or analytics events.
 */
export function fitContextFromSession(rawValue) {
  try {
    const state = typeof rawValue === 'string' ? JSON.parse(rawValue) : rawValue;
    const recommendation = recommendationFromSession(state);
    const heightCm = Number(state?.heightCm);
    const weightKg = Number(state?.weightKg);
    const torsoShape = ['lean', 'athletic', 'broad'].find(
      (candidate) => candidate === normalize(state?.torsoShape)
    );
    if (!recommendation || !Number.isFinite(heightCm) || !Number.isFinite(weightKg) || !torsoShape) return null;
    return {
      size: recommendation.size,
      lengthProfile: recommendation.lengthProfile,
      heightCm,
      weightKg,
      torsoShape,
      source: 'find-my-fit',
    };
  } catch {
    return null;
  }
}

export function lengthProfileFactoryReference(
  value,
  data = GARMENT_MEASUREMENTS
) {
  const offsets = data?.provisionalBodyLengthOffsetsCm;
  const status = String(offsets?.status || '').trim().toLowerCase();
  if (!PROVISIONAL_FIT_CONFIGURATION.lengthProfiles.some((profile) => profile.value === value)) return null;
  if (value === offsets?.referenceProfile) {
    return {
      value,
      status,
      text: 'The base garment length for each Size. Pending factory confirmation.',
    };
  }
  const offsetCm = Number(offsets?.[value]);
  if (!Number.isFinite(offsetCm) || offsetCm === 0) return null;
  const centimetres = Number(Math.abs(offsetCm).toFixed(1));
  const inches = Number((centimetres / CM_PER_INCH).toFixed(1));
  const direction = offsetCm < 0 ? 'shorter' : 'longer';
  return {
    value,
    status,
    offsetCm,
    offsetInches: inches,
    direction,
    text: `Approximately ${inches} in / ${centimetres} cm ${direction} than Regular. Pending factory confirmation.`,
  };
}

export function fitAvailabilityFromCatalog(catalog, size, lengthProfile) {
  if (!catalog || !Array.isArray(catalog.variants)) return null;
  const cartQuantities = new Map();
  for (const item of Array.isArray(catalog.cartItems) ? catalog.cartItems : []) {
    const id = normalizeId(item?.variantId);
    cartQuantities.set(id, (cartQuantities.get(id) || 0) + Math.max(0, Number(item?.quantity) || 0));
  }

  const matching = catalog.variants.filter((variant) => {
    const options = Array.isArray(variant?.selectedOptions) ? variant.selectedOptions : [];
    const optionValue = (names) =>
      options.find((option) => names.includes(normalize(option?.name)))?.value || '';
    return (
      normalize(optionValue(['size'])) === normalize(size) &&
      normalize(optionValue(['length profile', 'length', 'fit length'])) === normalize(lengthProfile)
    );
  });
  if (matching.length === 0) return false;

  return matching.some((variant) => {
    if (variant.available !== true) return false;
    if (variant.inventoryLimit == null) return true;
    return (cartQuantities.get(normalizeId(variant.variantId)) || 0) < Math.max(0, Number(variant.inventoryLimit) || 0);
  });
}

function createElement(tag, className = '', text = '') {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text) element.textContent = text;
  return element;
}

if (typeof globalThis.HTMLElement !== 'undefined' && typeof globalThis.customElements !== 'undefined') {
  class VelortenSizeGuide extends HTMLElement {
    #connected = false;
    #abortController = null;
    #config = {};
    #sections = [];
    #activeSection = 'recommended';
    #lastTrigger = null;
    #restoreTarget = 'trigger';
    #pendingFindMyFit = false;
    #applying = false;
    #unit = 'cm';

    connectedCallback() {
      if (this.#connected) return;
      this.#connected = true;
      this.#abortController = new AbortController();
      this.#config = this.#readConfig();
      this.#unit = this.#restoreUnit();
      this.#bindEvents();
      this.#configureSections();
      this.#renderTables();
      this.#renderRecommendation();
    }

    disconnectedCallback() {
      this.#connected = false;
      this.#abortController?.abort();
      this.#abortController = null;
      if (this.#dialog?.dataset.fallbackOpen === 'true') {
        this.#dialog.removeAttribute('open');
        delete this.#dialog.dataset.fallbackOpen;
      } else if (this.#dialog?.open) {
        this.#dialog.close('disconnected');
      }
      this.#unlockPage();
    }

    get #dialog() {
      return this.querySelector('dialog');
    }

    get #sectionRoot() {
      return this.closest('.shopify-section');
    }

    get #storageKey() {
      return `velorten_find_my_fit:v1:${this.dataset.productId || 'product'}`;
    }

    get #unitStorageKey() {
      return `velorten_size_guide_unit:v1:${this.dataset.productId || 'product'}`;
    }

    #readConfig() {
      try {
        return JSON.parse(this.querySelector('[data-size-guide-config]')?.textContent || '{}');
      } catch (error) {
        console.warn('[VELORTEN Size Guide] Configuration could not be read.', error);
        return {};
      }
    }

    #bindEvents() {
      const signal = this.#abortController.signal;
      document.addEventListener('click', this.#handleDocumentClick, { signal });
      document.addEventListener('velorten:size-guide:open', this.#handleOpenRequest, { signal });
      document.addEventListener('velorten:fit-applied', this.#handleFitApplied, { signal });
      this.#sectionRoot?.addEventListener('shopify:product:select', this.#handleProductSelect, { signal });
      window.addEventListener('pageshow', this.#handlePageShow, { signal });
      this.addEventListener('click', this.#handleClick, { signal });
      this.#dialog?.addEventListener('cancel', this.#handleCancel, { signal });
      this.#dialog?.addEventListener('close', this.#handleClose, { signal });
      this.#dialog?.addEventListener('click', this.#handleBackdropClick, { signal });
      this.#dialog?.addEventListener('keydown', this.#handleDialogKeydown, { signal });
    }

    #triggerControlsDialog(trigger) {
      return Boolean(
        trigger?.getAttribute?.('aria-controls')?.trim() === this.#dialog?.id && this.#dialog?.isConnected
      );
    }

    #handleDocumentClick = (event) => {
      if (
        this.#dialog?.dataset.fallbackOpen === 'true' &&
        event.target instanceof Node &&
        !this.#dialog.contains(event.target)
      ) {
        event.preventDefault();
        event.stopPropagation();
        this.#close('backdrop');
        return;
      }
      const trigger = event.target instanceof Element
        ? event.target.closest('[data-velorten-size-guide-trigger]')
        : null;
      if (!(trigger instanceof HTMLElement) || !this.#triggerControlsDialog(trigger)) return;
      event.preventDefault();
      this.#lastTrigger = trigger;
      this.#restoreTarget = 'trigger';
      this.#open();
    };

    #handleOpenRequest = (event) => {
      const detail = event?.detail || {};
      if (detail.sectionId && String(detail.sectionId) !== String(this.dataset.sectionId)) return;
      if (detail.productId && normalizeId(detail.productId) !== normalizeId(this.dataset.productId)) return;
      this.#lastTrigger = detail.returnTrigger instanceof HTMLElement
        ? detail.returnTrigger
        : detail.trigger instanceof HTMLElement
          ? detail.trigger
          : this.#matchingTrigger();
      this.#restoreTarget = 'trigger';
      this.#open('recommended');
    };

    #handleFitApplied = (event) => {
      if (event?.detail?.source !== 'find-my-fit') return;
      requestAnimationFrame(() => {
        this.#renderRecommendation();
        this.#renderTables();
      });
    };

    #handleProductSelect = (event) => {
      const rerender = () => {
        this.#renderRecommendation();
        this.#renderTables();
      };
      if (event.promise) Promise.resolve(event.promise).finally(() => requestAnimationFrame(rerender));
      else requestAnimationFrame(rerender);
    };

    #handlePageShow = () => {
      this.#renderRecommendation();
      this.#renderTables();
    };

    #handleClick = (event) => {
      const action = event.target instanceof Element ? event.target.closest('[data-size-guide-action]') : null;
      if (action instanceof HTMLElement) {
        if (action.dataset.sizeGuideAction === 'close') this.#close('close');
        if (action.dataset.sizeGuideAction === 'find-my-fit') this.#launchFindMyFit();
        if (action.dataset.sizeGuideAction === 'apply') this.#applyRecommendation();
        if (action.dataset.sizeGuideAction === 'measure') {
          this.#activateSection('measure', { focusTab: true, focusPanel: true });
        }
      }

      const unit = event.target instanceof Element ? event.target.closest('[data-size-guide-unit]') : null;
      if (unit instanceof HTMLButtonElement && this.contains(unit)) {
        this.#setUnit(unit.dataset.sizeGuideUnit || 'cm');
      }

      const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
      if (tab instanceof HTMLButtonElement && this.contains(tab)) {
        this.#activateSection(tab.dataset.sizeGuideSection || 'recommended', { focusTab: true });
      }

      const faq = event.target instanceof Element ? event.target.closest('[data-size-guide-faq]') : null;
      if (faq instanceof HTMLButtonElement && this.contains(faq)) {
        const expanded = faq.getAttribute('aria-expanded') === 'true';
        const panel = this.querySelector(`#${faq.getAttribute('aria-controls') || ''}`);
        faq.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (panel instanceof HTMLElement) panel.hidden = expanded;
      }
    };

    #handleCancel = (event) => {
      event.preventDefault();
      this.#close('escape');
    };

    #handleClose = () => {
      this.#unlockPage();
      if (this.#pendingFindMyFit) {
        this.#pendingFindMyFit = false;
        document.dispatchEvent(
          new CustomEvent('velorten:find-my-fit:open', {
            detail: {
              productId: this.dataset.productId,
              sectionId: this.dataset.sectionId,
              source: 'size-guide',
              returnTrigger: this.#lastTrigger,
            },
          })
        );
        return;
      }
      requestAnimationFrame(() => {
        if (this.#restoreTarget === 'selection') this.#focusProductSelection();
        else this.#matchingTrigger()?.focus({ preventScroll: true });
      });
    };

    #handleBackdropClick = (event) => {
      if (event.target === this.#dialog) this.#close('backdrop');
    };

    #handleDialogKeydown = (event) => {
      if (event.key === 'Escape' && this.#dialog?.dataset.fallbackOpen === 'true') {
        event.preventDefault();
        this.#close('escape');
        return;
      }

      const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
      if (tab instanceof HTMLButtonElement && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        const current = tabs.indexOf(tab);
        const targetIndex = event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? tabs.length - 1
            : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
        const target = tabs[targetIndex];
        if (target instanceof HTMLButtonElement) {
          this.#activateSection(target.dataset.sizeGuideSection || 'recommended', { focusTab: true });
        }
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = this.#focusableElements();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    #focusableElements() {
      return Array.from(
        this.#dialog?.querySelectorAll(
          'button:not([disabled]), a[href], select:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      ).filter((element) => element instanceof HTMLElement && !element.closest('[hidden]'));
    }

    #matchingTrigger() {
      if (this.#lastTrigger?.isConnected) return this.#lastTrigger;
      return Array.from(document.querySelectorAll('[data-velorten-size-guide-trigger][aria-controls]')).find(
        (trigger) => this.#triggerControlsDialog(trigger)
      ) || null;
    }

    #open(section = '') {
      const dialog = this.#dialog;
      if (!dialog?.isConnected || dialog.open) return;
      this.#configureSections();
      this.#renderRecommendation();
      this.#renderTables();
      const requestedSection = section || this.#activeSection;
      this.#activateSection(
        this.#sections.some((item) => item.value === requestedSection) ? requestedSection : 'recommended'
      );
      this.#lockPage();
      if (typeof dialog.showModal === 'function') {
        try {
          dialog.showModal();
        } catch (error) {
          this.#unlockPage();
          console.warn('[VELORTEN Size Guide] Dialog could not be opened.', error);
          return;
        }
      } else {
        dialog.setAttribute('open', '');
        dialog.dataset.fallbackOpen = 'true';
      }
      requestAnimationFrame(() => this.querySelector('.velorten-size-guide__header h2')?.focus({ preventScroll: true }));
    }

    #close(returnValue = '') {
      const dialog = this.#dialog;
      if (!dialog) return;
      if (dialog.dataset.fallbackOpen === 'true') {
        dialog.removeAttribute('open');
        delete dialog.dataset.fallbackOpen;
        this.#handleClose();
      } else if (dialog.open) {
        dialog.close(returnValue);
      }
    }

    #lockPage() {
      document.documentElement.classList.add('velorten-size-guide-is-open');
    }

    #unlockPage() {
      if (!document.querySelector('velorten-size-guide dialog[open]')) {
        document.documentElement.classList.remove('velorten-size-guide-is-open');
      }
    }

    #configureSections() {
      const hasQuestions = Boolean(this.querySelector('[data-size-guide-panel="questions"]'));
      this.#sections = availableGuideSections(this.#config, { hasQuestions });
      const tabs = this.querySelector('[data-size-guide-tabs]');
      tabs?.replaceChildren();

      for (const section of this.#sections) {
        const panel = this.querySelector(`[data-size-guide-panel="${section.value}"]`);
        if (!(panel instanceof HTMLElement)) continue;
        panel.removeAttribute('data-size-guide-unavailable');

        const button = createElement('button', '', section.label);
        button.type = 'button';
        button.id = `${this.id}-Tab-${section.value}`;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-controls', panel.id);
        button.setAttribute('aria-selected', 'false');
        button.tabIndex = -1;
        button.dataset.sizeGuideSection = section.value;
        tabs?.append(button);

        panel.setAttribute('aria-labelledby', button.id);
      }

      this.querySelectorAll('[data-size-guide-panel]').forEach((panel) => {
        const available = this.#sections.some((section) => section.value === panel.dataset.sizeGuidePanel);
        if (!available) {
          panel.hidden = true;
          panel.dataset.sizeGuideUnavailable = 'true';
        }
      });
      if (!this.#sections.some((section) => section.value === this.#activeSection)) {
        this.#activeSection = this.#sections[0]?.value || 'recommended';
      }
      this.#activateSection(this.#activeSection);
    }

    #activateSection(value, { focusTab = false, focusPanel = false } = {}) {
      if (!this.#sections.some((section) => section.value === value)) return;
      this.#activeSection = value;
      let activeTab = null;
      this.querySelectorAll('[data-size-guide-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.sizeGuidePanel !== value;
      });
      this.querySelectorAll('[role="tab"]').forEach((tab) => {
        const active = tab.dataset.sizeGuideSection === value;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        if (active) activeTab = tab;
      });
      this.querySelector('[data-size-guide-content]')?.scrollTo({ top: 0, behavior: 'auto' });
      if (activeTab instanceof HTMLButtonElement) {
        if (focusTab) activeTab.focus({ preventScroll: true });
        requestAnimationFrame(() => {
          const tablist = activeTab.closest('[role="tablist"]');
          if (!(tablist instanceof HTMLElement)) return;
          const leftEdge = activeTab.offsetLeft;
          const rightEdge = leftEdge + activeTab.offsetWidth;
          const visibleLeft = tablist.scrollLeft;
          const visibleRight = visibleLeft + tablist.clientWidth;
          let nextLeft = visibleLeft;
          if (leftEdge < visibleLeft) nextLeft = leftEdge;
          else if (rightEdge > visibleRight) nextLeft = rightEdge - tablist.clientWidth;
          if (nextLeft !== visibleLeft) {
            tablist.scrollTo({
              left: Math.max(0, nextLeft),
              behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            });
          }
        });
      }
      if (focusPanel) {
        requestAnimationFrame(() => {
          const panel = this.querySelector(`[data-size-guide-panel="${value}"]`);
          if (panel instanceof HTMLElement) panel.focus({ preventScroll: true });
        });
      }
    }

    #restoreUnit() {
      try {
        return sessionStorage.getItem(this.#unitStorageKey) === 'in' ? 'in' : 'cm';
      } catch {
        return 'cm';
      }
    }

    #setUnit(value) {
      const unit = value === 'in' ? 'in' : 'cm';
      if (unit === this.#unit) return;
      this.#unit = unit;
      try {
        sessionStorage.setItem(this.#unitStorageKey, unit);
      } catch {
        // Storage may be unavailable; the current modal instance still retains the choice.
      }
      this.#renderGarmentTable();
    }

    #recommendation() {
      try {
        return recommendationFromSession(sessionStorage.getItem(this.#storageKey));
      } catch {
        return null;
      }
    }

    #activeFit() {
      const picker = this.#sectionRoot?.querySelector('variant-picker');
      if (!picker) return null;
      const valueFor = (role) => {
        const fieldset = picker.querySelector(`fieldset[data-velorten-option="${role}"][data-velorten-confirmed="true"]`);
        if (!fieldset) return '';
        const input = fieldset.querySelector('input:checked');
        if (input instanceof HTMLInputElement) return input.value;
        const select = fieldset.querySelector('select');
        return select instanceof HTMLSelectElement ? select.value : '';
      };
      const size = valueFor('size');
      const lengthProfile = valueFor('length');
      return size && lengthProfile ? { size, lengthProfile } : null;
    }

    #fitLabel(fit) {
      const size = sizeDefinitionForValue(fit?.size);
      return size && fit?.lengthProfile ? `${size.displayName} · ${fit.lengthProfile}` : '';
    }

    #renderRecommendation() {
      const mount = this.querySelector('[data-size-guide-recommendation]');
      if (!mount) return;
      mount.replaceChildren();
      const recommendation = this.#recommendation();
      const active = this.#activeFit();

      if (!recommendation) {
        const wrapper = createElement('div', 'velorten-size-guide__no-recommendation');
        wrapper.append(
          createElement('h3', '', 'Not sure which fit is right?'),
          createElement('p', '', 'Answer three quick questions and we’ll recommend your VELORTEN size and Length Profile.')
        );
        if (active) {
          const selected = createElement('p', 'velorten-size-guide__status');
          selected.textContent = `Currently selected: ${this.#fitLabel(active)}`;
          wrapper.append(selected);
        }
        if (this.#config.findMyFitEnabled === true) {
          const actions = createElement('div', 'velorten-size-guide__actions');
          const button = createElement('button', 'velorten-size-guide__primary', 'Find My Fit →');
          button.type = 'button';
          button.dataset.sizeGuideAction = 'find-my-fit';
          actions.append(button);
          wrapper.append(actions);
        }
        mount.append(wrapper);
        return;
      }

      const label = this.#fitLabel(recommendation);
      const size = sizeDefinitionForValue(recommendation.size);
      const weightReference = weightReferenceForSize(recommendation.size);
      const lengthReference = lengthReferenceForProfile(recommendation.lengthProfile);
      const sameAsActive = Boolean(
        active && normalize(active.size) === normalize(recommendation.size) &&
        normalize(active.lengthProfile) === normalize(recommendation.lengthProfile)
      );
      const wrapper = createElement('div', 'velorten-size-guide__recommendation');
      wrapper.append(
        createElement('p', 'velorten-size-guide__eyebrow', 'YOUR RECOMMENDED FIT'),
        createElement('h3', 'velorten-size-guide__recommendation-name', label),
        createElement('p', 'velorten-size-guide__recommendation-copy', 'Best matched for balanced comfort, coverage and proportion.')
      );

      if (active) {
        const comparison = createElement('div', 'velorten-size-guide__comparison');
        if (sameAsActive) {
          comparison.append(createElement('p', '', 'Your recommended fit is currently selected.'));
        } else {
          const recommendedLine = createElement('p');
          const recommendedStrong = createElement('strong', '', 'Your recommendation: ');
          recommendedLine.append(recommendedStrong, document.createTextNode(label));
          const activeLine = createElement('p');
          const activeStrong = createElement('strong', '', 'Currently selected: ');
          activeLine.append(activeStrong, document.createTextNode(this.#fitLabel(active)));
          comparison.append(recommendedLine, activeLine);
        }
        wrapper.append(comparison);
      }

      const evidence = createElement('div', 'velorten-size-guide__fit-evidence');
      const sizeEvidence = createElement('article');
      sizeEvidence.append(
        createElement('h4', '', size?.displayName || recommendation.size),
        createElement('p', '', 'Best matched to your weight and torso profile.')
      );
      const lengthEvidence = createElement('article');
      lengthEvidence.append(
        createElement('h4', '', `${recommendation.lengthProfile} Length Profile`),
        createElement('p', '', 'Recommended for your height.')
      );
      evidence.append(sizeEvidence, lengthEvidence);
      wrapper.append(evidence);

      const references = createElement('div', 'velorten-size-guide__reference');
      if (weightReference) references.append(createElement('p', '', `Weight reference: ${weightReference.metric} · ${weightReference.imperial}`));
      if (lengthReference) references.append(createElement('p', '', `Height reference: ${lengthReference.metric} · ${lengthReference.imperial}`));
      wrapper.append(references);

      const actions = createElement('div', 'velorten-size-guide__actions');
      if (!sameAsActive && this.#sectionRoot?.querySelector('variant-picker')) {
        const apply = createElement('button', 'velorten-size-guide__primary', `Apply ${label}`);
        apply.type = 'button';
        apply.dataset.sizeGuideAction = 'apply';
        apply.disabled = this.#applying;
        actions.append(apply);
      }
      if (this.#config.findMyFitEnabled === true) {
        const adjust = createElement('button', 'velorten-size-guide__secondary', 'Adjust My Answers');
        adjust.type = 'button';
        adjust.dataset.sizeGuideAction = 'find-my-fit';
        actions.append(adjust);
      }
      if (actions.childElementCount > 0) wrapper.append(actions);
      const error = createElement('p', 'velorten-size-guide__error');
      error.dataset.sizeGuideError = 'true';
      error.setAttribute('role', 'status');
      error.hidden = true;
      wrapper.append(error);
      mount.append(wrapper);
    }

    #renderTables() {
      const recommendation = this.#recommendation();
      const sizeRows = this.querySelector('[data-size-guide-size-rows]');
      sizeRows?.replaceChildren(
        ...PROVISIONAL_FIT_CONFIGURATION.sizes.map((size) => {
          const reference = weightTableReferenceForSize(size.value);
          const row = document.createElement('tr');
          const recommended = recommendation?.size === size.value;
          row.dataset.recommended = recommended ? 'true' : 'false';
          const heading = document.createElement('th');
          heading.scope = 'row';
          heading.textContent = size.value;
          if (recommended) heading.append(createElement('span', 'velorten-size-guide__recommended-label', 'Recommended'));
          const metric = createElement('td', '', reference?.metric || '');
          const imperial = createElement('td', '', reference?.imperial.replace(/^approximately\s+/, '') || '');
          row.append(heading, metric, imperial);
          return row;
        })
      );

      const lengthRows = this.querySelector('[data-size-guide-length-rows]');
      lengthRows?.replaceChildren(
        ...PROVISIONAL_FIT_CONFIGURATION.lengthProfiles.map((profile) => {
          const reference = lengthTableReferenceForProfile(profile.value);
          const row = document.createElement('tr');
          const recommended = recommendation?.lengthProfile === profile.value;
          row.dataset.recommended = recommended ? 'true' : 'false';
          const heading = document.createElement('th');
          heading.scope = 'row';
          heading.textContent = profile.displayName || profile.value;
          if (recommended) heading.append(createElement('span', 'velorten-size-guide__recommended-label', 'Recommended'));
          const metric = createElement('td', '', reference?.metric || '');
          const imperial = createElement('td', '', reference?.imperial || '');
          row.append(heading, metric, imperial);
          return row;
        })
      );

      this.#renderInternationalTable();
      this.#renderGarmentTable();
      this.#renderLengthProfileReferences();
    }

    #renderInternationalTable() {
      const tbody = this.querySelector('[data-size-guide-international-rows]');
      if (!tbody) return;
      const recommendation = this.#recommendation();
      const published = this.#config.internationalEnabled === true && validInternationalConversionData();
      const pending = this.querySelector('[data-size-guide-international-pending]');
      if (pending instanceof HTMLElement) pending.hidden = published;
      const sourceRows = new Map(
        INTERNATIONAL_SIZE_CONVERSIONS.rows.map((row) => [String(row?.VELORTEN || ''), row])
      );
      tbody.replaceChildren(
        ...PROVISIONAL_FIT_CONFIGURATION.sizes.map((size) => {
          const dataRow = published ? sourceRows.get(size.value) : null;
          const row = document.createElement('tr');
          const recommended = recommendation?.size === size.value;
          row.dataset.recommended = recommended ? 'true' : 'false';
          const heading = createElement('th', '', size.value);
          heading.scope = 'row';
          if (recommended) heading.append(createElement('span', 'velorten-size-guide__recommended-label', 'Recommended'));
          row.append(heading);
          INTERNATIONAL_SIZE_CONVERSIONS.columns.slice(1).forEach((column) => {
            row.append(createElement('td', '', provisionalValueForDisplay(dataRow?.[column])));
          });
          return row;
        })
      );
    }

    #renderGarmentTable() {
      const tbody = this.querySelector('[data-size-guide-garment-rows]');
      if (!tbody) return;
      const recommendation = this.#recommendation();
      const published = this.#config.garmentEnabled === true && validGarmentMeasurementData();
      const pending = this.querySelector('[data-size-guide-garment-pending]');
      if (pending instanceof HTMLElement) pending.hidden = published;
      this.querySelectorAll('[data-size-guide-unit]').forEach((button) => {
        button.setAttribute('aria-pressed', button.dataset.sizeGuideUnit === this.#unit ? 'true' : 'false');
      });
      const sourceRows = new Map(GARMENT_MEASUREMENTS.rows.map((row) => [String(row?.size || ''), row]));
      tbody.replaceChildren(
        ...PROVISIONAL_FIT_CONFIGURATION.sizes.map((sizeDefinition) => {
          const dataRow = published ? sourceRows.get(sizeDefinition.value) : null;
          const row = document.createElement('tr');
          const recommended = recommendation?.size === sizeDefinition.value;
          row.dataset.recommended = recommended ? 'true' : 'false';
          const size = createElement('th', '', sizeDefinition.value);
          size.scope = 'row';
          if (recommended) size.append(createElement('span', 'velorten-size-guide__recommended-label', 'Recommended'));
          row.append(size);
          GARMENT_MEASUREMENTS.fields.forEach((field) => {
            row.append(createElement('td', '', formatGarmentMeasurement(dataRow?.[field.key], this.#unit)));
          });
          return row;
        })
      );
    }

    #renderLengthProfileReferences() {
      const mount = this.querySelector('[data-size-guide-international-lengths]');
      const recommendation = this.#recommendation();
      if (mount) {
        mount.replaceChildren(
          ...PROVISIONAL_FIT_CONFIGURATION.lengthProfiles.map((profile) => {
            const reference = lengthProfileFactoryReference(profile.value);
            const card = createElement('article', 'velorten-size-guide__length-card');
            const recommended = recommendation?.lengthProfile === profile.value;
            card.dataset.recommended = recommended ? 'true' : 'false';
            const heading = createElement('h4', '', profile.displayName || profile.value);
            if (recommended) heading.append(createElement('span', 'velorten-size-guide__recommended-label', 'Recommended'));
            card.append(heading, createElement('p', '', reference?.text || 'Pending factory confirmation.'));
            return card;
          })
        );
      }

      const note = this.querySelector('[data-size-guide-garment-length-note]');
      const short = lengthProfileFactoryReference('Short');
      const tall = lengthProfileFactoryReference('Tall');
      if (note && short && tall) {
        note.textContent =
          `Short is intended to be approximately ${short.offsetInches} in / ${Math.abs(short.offsetCm)} cm shorter than Regular. ` +
          `Tall is intended to be approximately ${tall.offsetInches} in / ${Math.abs(tall.offsetCm)} cm longer than Regular. ` +
          'These relationships remain pending factory confirmation.';
      }
    }

    #bundleCatalog() {
      for (const node of this.#sectionRoot?.querySelectorAll('script[data-velorten-bundle-data]') || []) {
        try {
          const candidate = JSON.parse(node.textContent || '{}');
          if (normalizeId(candidate.productId) === normalizeId(this.dataset.productId)) return candidate;
        } catch {
          // Ignore malformed or unrelated payloads.
        }
      }
      return null;
    }

    async #applyRecommendation() {
      if (this.#applying) return;
      const recommendation = this.#recommendation();
      if (!recommendation) return;
      const availability = fitAvailabilityFromCatalog(
        this.#bundleCatalog(),
        recommendation.size,
        recommendation.lengthProfile
      );
      if (availability === false) {
        this.#showError('This fit is currently unavailable. Try another Size or Length Profile, or check back soon.');
        return;
      }

      this.#applying = true;
      this.#renderRecommendation();
      const settlement = this.#waitForNativeSelection();
      document.dispatchEvent(
        new CustomEvent('velorten:fit:confirmed', {
          detail: {
            productId: this.dataset.productId,
            sectionId: this.dataset.sectionId,
            size: recommendation.size,
            length: recommendation.lengthProfile,
            source: 'size-guide',
          },
        })
      );
      await settlement;
      this.#applying = false;
      this.dispatchEvent(
        new CustomEvent('velorten:fit-applied', {
          bubbles: true,
          detail: {
            size: recommendation.size,
            lengthProfile: recommendation.lengthProfile,
            source: 'find-my-fit',
          },
        })
      );
      const label = this.#fitLabel(recommendation);
      const live = this.querySelector('[data-size-guide-live]');
      if (live) live.textContent = `${label} applied. Choose a colour to continue.`;
      this.#restoreTarget = 'selection';
      this.#close('applied');
    }

    #showError(message) {
      const error = this.querySelector('[data-size-guide-error]');
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    }

    #waitForNativeSelection() {
      return new Promise((resolve) => {
        const root = this.#sectionRoot;
        let settled = false;
        let timer = null;
        const finish = () => {
          if (settled) return;
          settled = true;
          if (timer) clearTimeout(timer);
          root?.removeEventListener('shopify:product:select', handleSelect);
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        };
        const handleSelect = (event) => Promise.resolve(event.promise).finally(finish);
        root?.addEventListener('shopify:product:select', handleSelect, { once: true });
        timer = setTimeout(finish, 650);
      });
    }

    #launchFindMyFit() {
      if (this.#config.findMyFitEnabled !== true || !this.dataset.findMyFitDialogId) return;
      this.#pendingFindMyFit = true;
      this.#close('find-my-fit');
    }

    #focusProductSelection() {
      const picker = this.#sectionRoot?.querySelector('variant-picker');
      const size = picker?.querySelector('fieldset[data-velorten-option="size"]');
      const target = size?.querySelector('input:not(:disabled), select:not(:disabled), button:not(:disabled)');
      picker?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    }
  }

  if (!customElements.get('velorten-size-guide')) {
    customElements.define('velorten-size-guide', VelortenSizeGuide);
  }
}
